import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCar,
  FaPlus,
  FaTrash,
  FaEdit,
  FaSpinner,
  FaLayerGroup,
  FaEye,
  FaTimes,
  FaChevronDown,
  FaFilter,
  FaTags,
  FaWrench,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { adminVehicleCatalogService, authService, categoryService, productService } from '../../../../services/apiService';
import { getApiErrorMessage } from '../../../../utils/api';
import { toast } from 'react-toastify';

const FUEL_OPTIONS = [
  '',
  'Petrol',
  'Diesel',
  'CNG',
  'LPG',
  'Electric',
  'Hybrid',
  'Plug-in hybrid'
];
const TRANSMISSION_OPTIONS = ['', 'Automatic', 'Manual'];

const EMPTY_MODEL_FORM = {
  name: '',
  generation: '',
  generationYear: '',
  variant: '',
  fuelType: '',
  transmissionType: '',
  imageUrls: [],
  /** Superadmin Categories — string ids, multi-select */
  productCategoryIds: []
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const commaIndex = typeof result === 'string' ? result.indexOf(',') : -1;
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

/** Merge API model with legacy single `productCategoryId` field if present */
const modelProductCategoryIdsFromApi = (mod) => {
  if (!mod) return [];
  const fromArr = Array.isArray(mod.productCategoryIds) ? mod.productCategoryIds : [];
  const ids = fromArr.map((x) => String(x));
  if (mod.productCategoryId && !ids.includes(String(mod.productCategoryId))) {
    ids.push(String(mod.productCategoryId));
  }
  return ids;
};

const toggleCategoryIdInList = (list, id, checked) => {
  const s = String(id);
  const next = new Set((list || []).map(String));
  if (checked) next.add(s);
  else next.delete(s);
  return [...next];
};

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const formatShortDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return '—';
  }
};

const EMPTY_CATALOG_FILTERS = {
  makerId: '',
  status: '',
  model: '',
  generation: '',
  year: '',
  fuel: '',
  variant: ''
};

const trimStr = (v) => (v == null ? '' : String(v).trim());

const yearMatchesFilter = (filterStr, generationYear, trims) => {
  const q = trimStr(filterStr);
  if (!q) return true;
  const asNum = Number(q);
  const isExactYear =
    Number.isFinite(asNum) &&
    /^\s*\d+\s*$/.test(q) &&
    asNum >= 1980 &&
    asNum <= 2100;
  if (isExactYear) {
    if (generationYear != null && Number(generationYear) === asNum) return true;
    return (trims || []).some((t) => Number(t.year) === asNum);
  }
  const g = generationYear != null ? String(generationYear) : '';
  if (g.includes(q)) return true;
  return (trims || []).some((t) => String(t.year ?? '').includes(q));
};

const modelMatchesCatalogFilters = (mod, f, trimsForModel) => {
  if (trimStr(f.model) && !(mod.name || '').toLowerCase().includes(f.model.toLowerCase())) {
    return false;
  }
  if (
    trimStr(f.generation) &&
    !(String(mod.generation || '').toLowerCase().includes(f.generation.toLowerCase()))
  ) {
    return false;
  }
  if (trimStr(f.fuel) && (mod.fuelType || '') !== f.fuel) {
    return false;
  }
  if (!yearMatchesFilter(f.year, mod.generationYear, trimsForModel)) {
    return false;
  }
  if (trimStr(f.variant)) {
    const q = f.variant.toLowerCase();
    const mv = String(mod.variant || '').toLowerCase();
    if (mv.includes(q)) return true;
    return (trimsForModel || []).some((t) =>
      String(t.variant || '').toLowerCase().includes(q)
    );
  }
  return true;
};

const ADMIN_CATEGORIES_PANEL_PATH = '/admin/dashboard/categories';

const VehicleCatalog = ({
  hideInlineAddForms = false,
  embedded = false,
  hideTitle = false,
  refreshKey = 0
}) => {
  const navigate = useNavigate();
  const [makers, setMakers] = useState([]);
  const [loadingMakers, setLoadingMakers] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newMakerName, setNewMakerName] = useState('');
  const [expandedMakerId, setExpandedMakerId] = useState('');
  const [modelsByMaker, setModelsByMaker] = useState({});
  const [loadingModelsFor, setLoadingModelsFor] = useState({});
  const [expandedModelId, setExpandedModelId] = useState('');
  /** Chevron on model row: categories + spare parts (same pattern as maker → models) */
  const [expandedModelCategoriesId, setExpandedModelCategoriesId] = useState('');
  const [trimsByModel, setTrimsByModel] = useState({});
  const [loadingTrimsFor, setLoadingTrimsFor] = useState({});
  const [categoriesByModel, setCategoriesByModel] = useState({});
  const [loadingCategoriesFor, setLoadingCategoriesFor] = useState({});
  const [partsByCategory, setPartsByCategory] = useState({});
  const [loadingPartsFor, setLoadingPartsFor] = useState({});
  const [productsByModel, setProductsByModel] = useState({});
  const [loadingProductsFor, setLoadingProductsFor] = useState({});
  const [expandedCategoryId, setExpandedCategoryId] = useState('');
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [newPart, setNewPart] = useState({ name: '', partNumber: '', notes: '' });
  const [editingPart, setEditingPart] = useState(null);
  const [viewPartModal, setViewPartModal] = useState(null);
  const [uploadingPartImages, setUploadingPartImages] = useState(false);

  const [addModelModal, setAddModelModal] = useState(null);
  const [addModelForm, setAddModelForm] = useState(() => ({ ...EMPTY_MODEL_FORM }));
  const [uploadingAddModelImages, setUploadingAddModelImages] = useState(false);

  const [editMakerModal, setEditMakerModal] = useState(null);
  const [editMakerName, setEditMakerName] = useState('');
  const [editModalModels, setEditModalModels] = useState([]);
  const [loadingEditModalModels, setLoadingEditModalModels] = useState(false);
  const [newModelDraft, setNewModelDraft] = useState(() => ({ ...EMPTY_MODEL_FORM }));
  const [uploadingEditModelImages, setUploadingEditModelImages] = useState(false);
  const [showAddModelInEdit, setShowAddModelInEdit] = useState(false);

  const [editingModel, setEditingModel] = useState(null);
  /** { model, maker } — read-only details popup */
  const [viewModelModal, setViewModelModal] = useState(null);
  const [editingTrim, setEditingTrim] = useState(null);
  const [newTrim, setNewTrim] = useState({
    generation: '',
    year: new Date().getFullYear(),
    month: 1,
    variant: ''
  });

  const [catalogFilters, setCatalogFilters] = useState(() => ({ ...EMPTY_CATALOG_FILTERS }));
  const [debouncedCatalogFilters, setDebouncedCatalogFilters] = useState(() => ({
    ...EMPTY_CATALOG_FILTERS
  }));
  const [filterIndexLoading, setFilterIndexLoading] = useState(false);
  /** Flat list from GET /categories/flat — same source as superadmin Categories page */
  const [productCategories, setProductCategories] = useState([]);

  const sortedProductCategories = useMemo(() => {
    const list = Array.isArray(productCategories) ? [...productCategories] : [];
    return list.sort((a, b) => {
      const na = String(a.name ?? a ?? '').toLowerCase();
      const nb = String(b.name ?? b ?? '').toLowerCase();
      return na.localeCompare(nb);
    });
  }, [productCategories]);

  const productCategoryLabel = useCallback(
    (id) => {
      if (id == null || id === '') return '—';
      const sid = String(id);
      const cat = productCategories.find((c) => String(c._id || c.id) === sid);
      if (cat) return cat.name ?? cat;
      return '—';
    },
    [productCategories]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fastResult = await categoryService.getAllActiveCategoriesFlat({ forVendor: true });
        let list = [];
        if (fastResult?.categories && Array.isArray(fastResult.categories)) {
          list = fastResult.categories;
        } else if (Array.isArray(fastResult)) {
          list = fastResult;
        } else if (fastResult?.data?.categories && Array.isArray(fastResult.data.categories)) {
          list = fastResult.data.categories;
        } else if (fastResult?.data && Array.isArray(fastResult.data)) {
          list = fastResult.data;
        }
        if (!cancelled) setProductCategories(list);
      } catch (e) {
        if (!cancelled) {
          toast.error(getApiErrorMessage(e, 'Could not load product categories for the dropdown'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMakers = useCallback(async () => {
    try {
      setLoadingMakers(true);
      const data = await adminVehicleCatalogService.listMakers();
      setMakers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load makers'));
    } finally {
      setLoadingMakers(false);
    }
  }, []);

  const fetchModels = useCallback(async (makerId) => {
    if (!makerId) return [];
    try {
      setLoadingModelsFor((prev) => ({ ...prev, [makerId]: true }));
      const data = await adminVehicleCatalogService.listModels(makerId);
      const list = Array.isArray(data) ? data : [];
      setModelsByMaker((prev) => ({ ...prev, [makerId]: list }));
      return list;
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load models'));
      return [];
    } finally {
      setLoadingModelsFor((prev) => ({ ...prev, [makerId]: false }));
    }
  }, []);

  const fetchTrims = useCallback(async (modelId) => {
    if (!modelId) return [];
    try {
      setLoadingTrimsFor((prev) => ({ ...prev, [modelId]: true }));
      const data = await adminVehicleCatalogService.listTrims(modelId);
      const list = Array.isArray(data) ? data : [];
      setTrimsByModel((prev) => ({ ...prev, [modelId]: list }));
      return list;
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load variants'));
      return [];
    } finally {
      setLoadingTrimsFor((prev) => ({ ...prev, [modelId]: false }));
    }
  }, []);

  const fetchCategories = useCallback(async (modelId) => {
    if (!modelId) return [];
    try {
      setLoadingCategoriesFor((prev) => ({ ...prev, [modelId]: true }));
      const data = await adminVehicleCatalogService.listCategories(modelId);
      const list = Array.isArray(data) ? data : [];
      setCategoriesByModel((prev) => ({ ...prev, [modelId]: list }));
      return list;
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load categories'));
      return [];
    } finally {
      setLoadingCategoriesFor((prev) => ({ ...prev, [modelId]: false }));
    }
  }, []);

  const fetchParts = useCallback(async (categoryId, modelId = '') => {
    if (!categoryId) return [];
    const cacheKey = `${String(modelId || '')}::${String(categoryId)}`;
    try {
      setLoadingPartsFor((prev) => ({ ...prev, [cacheKey]: true }));
      const data = await adminVehicleCatalogService.listParts(categoryId, {
        modelId: modelId || undefined
      });
      const list = Array.isArray(data) ? data : [];
      setPartsByCategory((prev) => ({ ...prev, [cacheKey]: list }));
      return list;
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load spare parts'));
      return [];
    } finally {
      setLoadingPartsFor((prev) => ({ ...prev, [cacheKey]: false }));
    }
  }, []);

  const fetchModelProducts = useCallback(async (modelId) => {
    if (!modelId) return [];
    try {
      setLoadingProductsFor((prev) => ({ ...prev, [modelId]: true }));
      const data = await adminVehicleCatalogService.listModelProducts(modelId, { limit: 200 });
      const list = Array.isArray(data) ? data : [];
      setProductsByModel((prev) => ({ ...prev, [modelId]: list }));
      return list;
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load model products'));
      return [];
    } finally {
      setLoadingProductsFor((prev) => ({ ...prev, [modelId]: false }));
    }
  }, []);

  useEffect(() => {
    loadMakers();
  }, [loadMakers, refreshKey]);

  // Keep per-maker model counts visible even before opening a maker row.
  useEffect(() => {
    const missingMakerIds = (makers || [])
      .map((m) => String(m._id))
      .filter((makerId) => makerId && !Array.isArray(modelsByMaker[makerId]) && !loadingModelsFor[makerId]);
    if (!missingMakerIds.length) return;

    let cancelled = false;
    (async () => {
      await Promise.all(
        missingMakerIds.map(async (makerId) => {
          if (cancelled) return;
          await fetchModels(makerId);
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [makers, modelsByMaker, loadingModelsFor, fetchModels]);

  useEffect(() => {
    setExpandedCategoryId('');
    setEditingCategory(null);
    setEditingPart(null);
    setNewCategory({ name: '', description: '' });
    setNewPart({ name: '', partNumber: '', notes: '' });
  }, [expandedModelCategoriesId]);

  useEffect(() => {
    if (!expandedCategoryId) return;
    fetchParts(expandedCategoryId);
  }, [expandedCategoryId, fetchParts]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCatalogFilters({ ...catalogFilters }), 350);
    return () => clearTimeout(t);
  }, [catalogFilters]);

  const needsDeepCatalogData = useMemo(() => {
    const f = debouncedCatalogFilters;
    return !!(
      trimStr(f.model) ||
      trimStr(f.generation) ||
      trimStr(f.year) ||
      trimStr(f.fuel) ||
      trimStr(f.variant)
    );
  }, [debouncedCatalogFilters]);

  const needsTrimDataForFilter = useMemo(() => {
    const f = debouncedCatalogFilters;
    return !!(trimStr(f.year) || trimStr(f.variant));
  }, [debouncedCatalogFilters]);

  useEffect(() => {
    if (!needsDeepCatalogData && !needsTrimDataForFilter) return;
    if (!makers.length) return;
    let cancelled = false;
    (async () => {
      setFilterIndexLoading(true);
      try {
        const modelLists = await Promise.all(makers.map((m) => fetchModels(m._id)));
        if (cancelled) return;
        if (needsTrimDataForFilter) {
          const allModels = modelLists.flat();
          await Promise.all(allModels.map((mo) => fetchTrims(mo._id)));
        }
      } finally {
        if (!cancelled) setFilterIndexLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    makers,
    refreshKey,
    needsDeepCatalogData,
    needsTrimDataForFilter,
    fetchModels,
    fetchTrims
  ]);

  const loadModelsForEditModal = useCallback(async (makerId) => {
    if (!makerId) {
      setEditModalModels([]);
      return;
    }
    try {
      setLoadingEditModalModels(true);
      const data = await adminVehicleCatalogService.listModels(makerId);
      setEditModalModels(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load models'));
    } finally {
      setLoadingEditModalModels(false);
    }
  }, []);

  useEffect(() => {
    if (!editMakerModal) {
      setEditMakerName('');
      setEditModalModels([]);
      setNewModelDraft({ ...EMPTY_MODEL_FORM });
      setShowAddModelInEdit(false);
      return;
    }
    setEditMakerName(editMakerModal.name || '');
    loadModelsForEditModal(editMakerModal._id);
  }, [editMakerModal, loadModelsForEditModal]);

  const monthLabel = (n) => MONTHS.find((x) => x.value === n)?.label || n;

  const onAddMaker = async (e) => {
    e.preventDefault();
    const name = newMakerName.trim();
    if (!name) return;
    try {
      setSaving(true);
      await adminVehicleCatalogService.createMaker({ name });
      setNewMakerName('');
      toast.success('Car maker added');
      await loadMakers();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not add maker');
    } finally {
      setSaving(false);
    }
  };

  const toggleMakerView = async (makerId) => {
    if (expandedMakerId === String(makerId)) {
      setExpandedMakerId('');
      setExpandedModelId('');
      setExpandedModelCategoriesId('');
      return;
    }
    setExpandedMakerId(String(makerId));
    setExpandedModelId('');
    setExpandedModelCategoriesId('');
    await fetchModels(makerId);
  };

  const toggleModelView = async (modelId) => {
    if (expandedModelId === String(modelId)) {
      setExpandedModelId('');
      return;
    }
    setExpandedModelId(String(modelId));
    await Promise.all([fetchTrims(modelId), fetchModelProducts(modelId)]);
  };

  const openViewModelDetails = async (mod, makerDoc) => {
    setViewModelModal({ model: mod, maker: makerDoc });
    await fetchTrims(mod._id);
  };

  const closeViewModelDetails = () => setViewModelModal(null);

  const toggleModelCategoriesView = async (modelId) => {
    if (expandedModelCategoriesId === String(modelId)) {
      setExpandedModelCategoriesId('');
      return;
    }
    const key = String(modelId);
    setExpandedModelCategoriesId(key);

    const cachedCategories = categoriesByModel[key];
    const categories = Array.isArray(cachedCategories)
      ? cachedCategories
      : await fetchCategories(modelId);

    await Promise.all(
      (Array.isArray(categories) ? categories : [])
        .map((cat) => String(cat?._id || cat?.id || '').trim())
        .filter(Boolean)
        .filter((categoryId) => !Array.isArray(partsByCategory[`${String(modelId)}::${categoryId}`]))
        .map((categoryId) => fetchParts(categoryId, modelId))
    );
  };

  const openAddModelModal = (maker) => {
    setAddModelModal({ makerId: maker._id, makerName: maker.name });
    setAddModelForm({ ...EMPTY_MODEL_FORM });
  };

  const buildModelPayload = (form) => {
    const name = (form.name || '').trim();
    const generation = (form.generation || '').trim();
    const variant = (form.variant || '').trim();
    const fuelType = (form.fuelType || '').trim();
    const transmissionType = (form.transmissionType || '').trim();
    const gyRaw = form.generationYear;
    const payload = { name };
    if (generation) payload.generation = generation;
    if (variant) payload.variant = variant;
    if (fuelType) payload.fuelType = fuelType;
    if (transmissionType) payload.transmissionType = transmissionType;
    if (gyRaw !== '' && gyRaw != null) {
      const gy = Number(gyRaw);
      if (Number.isFinite(gy)) payload.generationYear = gy;
    }
    if (Array.isArray(form.imageUrls) && form.imageUrls.length > 0) {
      payload.imageUrls = [...new Set(form.imageUrls.map((u) => String(u || '').trim()).filter(Boolean))];
    }
    payload.productCategoryIds = Array.isArray(form.productCategoryIds)
      ? form.productCategoryIds.map(String)
      : [];
    return payload;
  };

  const uploadModelImages = async (filesLike, target) => {
    const files = Array.from(filesLike || []);
    if (!files.length) return;
    const setUploading = target === 'add' ? setUploadingAddModelImages : setUploadingEditModelImages;
    const setForm = target === 'add' ? setAddModelForm : setNewModelDraft;
    try {
      setUploading(true);
      const uploaded = [];
      for (const file of files) {
        const base64 = await readFileAsBase64(file);
        const uploadedUrl = await authService.uploadFile(
          base64,
          file.name,
          file.type || 'image/jpeg'
        );
        uploaded.push(String(uploadedUrl || '').trim());
      }
      setForm((f) => ({
        ...f,
        imageUrls: [...new Set([...(f.imageUrls || []), ...uploaded.filter(Boolean)])]
      }));
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? 's' : ''} uploaded.`);
    } catch (err) {
      toast.error(err.message || 'Could not upload image(s).');
    } finally {
      setUploading(false);
    }
  };

  const uploadPartImages = async (filesLike) => {
    const files = Array.from(filesLike || []);
    if (!files.length || !editingPart) return;
    try {
      setUploadingPartImages(true);
      const uploaded = [];
      for (const file of files) {
        const base64 = await readFileAsBase64(file);
        const uploadedUrl = await authService.uploadFile(
          base64,
          file.name,
          file.type || 'image/jpeg'
        );
        uploaded.push(String(uploadedUrl || '').trim());
      }
      const validUploaded = uploaded.filter(Boolean);
      if (validUploaded.length) {
        setEditingPart((prev) => {
          if (!prev) return prev;
          const existing = [
            ...(Array.isArray(prev.imageUrls) ? prev.imageUrls : []),
            ...(Array.isArray(prev.images) ? prev.images : []),
            prev.imageUrl || ''
          ]
            .map((u) => String(u || '').trim())
            .filter(Boolean);
          // Keep newly uploaded files first so a stale old URL does not stay primary.
          const merged = [...new Set([...validUploaded, ...existing])];
          return {
            ...prev,
            imageUrls: merged,
            imageUrl: merged[0] || ''
          };
        });
      }
      toast.success(`${validUploaded.length} image${validUploaded.length > 1 ? 's' : ''} uploaded.`);
    } catch (err) {
      toast.error(err.message || 'Could not upload image(s).');
    } finally {
      setUploadingPartImages(false);
    }
  };

  const submitAddModelModal = async (e) => {
    e.preventDefault();
    if (!addModelModal) return;
    const name = (addModelForm.name || '').trim();
    if (!name) {
      toast.error('Enter a model name');
      return;
    }
    const gyCheck = addModelForm.generationYear;
    if (gyCheck !== '' && gyCheck != null) {
      const n = Number(gyCheck);
      if (Number.isFinite(n) && (n < 1980 || n > 2100)) {
        toast.error('Model year must be between 1980 and 2100, or leave empty.');
        return;
      }
    }
    const makerId = addModelModal.makerId;
    try {
      setSaving(true);
      await adminVehicleCatalogService.createModel(makerId, buildModelPayload(addModelForm));
      toast.success('Model added');
      setAddModelModal(null);
      setAddModelForm({ ...EMPTY_MODEL_FORM });
      await fetchModels(makerId);
      if (editMakerModal && String(editMakerModal._id) === String(makerId)) {
        await loadModelsForEditModal(makerId);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not add model');
    } finally {
      setSaving(false);
    }
  };

  const openEditMakerModal = (m) => {
    setEditMakerModal(m);
    setShowAddModelInEdit(false);
    setNewModelDraft({ ...EMPTY_MODEL_FORM });
  };

  const saveMakerFromModal = async () => {
    if (!editMakerModal) return;
    const name = editMakerName.trim();
    if (!name) {
      toast.error('Maker name is required');
      return;
    }
    try {
      setSaving(true);
      await adminVehicleCatalogService.updateMaker(editMakerModal._id, { name });
      toast.success('Car maker updated');
      setEditMakerModal(null);
      await loadMakers();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const addModelFromEditModal = async (e) => {
    e.preventDefault();
    if (!editMakerModal) return;
    const name = (newModelDraft.name || '').trim();
    if (!name) {
      toast.error('Model name is required');
      return;
    }
    const gyCheck = newModelDraft.generationYear;
    if (gyCheck !== '' && gyCheck != null) {
      const n = Number(gyCheck);
      if (Number.isFinite(n) && (n < 1980 || n > 2100)) {
        toast.error('Model year must be between 1980 and 2100, or leave empty.');
        return;
      }
    }
    try {
      setSaving(true);
      await adminVehicleCatalogService.createModel(editMakerModal._id, buildModelPayload(newModelDraft));
      setNewModelDraft({ ...EMPTY_MODEL_FORM });
      setShowAddModelInEdit(false);
      toast.success('Model added');
      await loadModelsForEditModal(editMakerModal._id);
      await fetchModels(editMakerModal._id);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not add model');
    } finally {
      setSaving(false);
    }
  };

  const deleteModelFromEditModal = async (m) => {
    if (!window.confirm(`Delete model "${m.name}" and all year/month/variant rows?`)) return;
    if (!editMakerModal) return;
    try {
      setSaving(true);
      await adminVehicleCatalogService.deleteModel(m._id);
      toast.success('Model deleted');
      await loadModelsForEditModal(editMakerModal._id);
      await fetchModels(editMakerModal._id);
      if (String(expandedModelId) === String(m._id)) setExpandedModelId('');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const onAddTrim = async (e) => {
    e.preventDefault();
    if (!expandedModelId) {
      toast.error('Select a model first');
      return;
    }
    const generation = (newTrim.generation || '').trim();
    const variant = newTrim.variant.trim();
    if (!generation) {
      toast.error('Enter generation');
      return;
    }
    if (!variant) {
      toast.error('Enter a variant / trim name');
      return;
    }
    try {
      setSaving(true);
      await adminVehicleCatalogService.createTrim(expandedModelId, {
        generation,
        year: Number(newTrim.year),
        month: Number(newTrim.month),
        variant
      });
      setNewTrim((t) => ({ ...t, generation: '', variant: '' }));
      toast.success('Generation / year / month / variant added');
      await fetchTrims(expandedModelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not add variant');
    } finally {
      setSaving(false);
    }
  };

  const toggleMakerActive = async (m, e) => {
    e.stopPropagation();
    try {
      setSaving(true);
      await adminVehicleCatalogService.updateMaker(m._id, { isActive: !m.isActive });
      await loadMakers();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleModelActive = async (m, makerId) => {
    try {
      setSaving(true);
      await adminVehicleCatalogService.updateModel(m._id, { isActive: !m.isActive });
      await fetchModels(makerId);
      if (editMakerModal && String(editMakerModal._id) === String(makerId)) {
        await loadModelsForEditModal(makerId);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleTrimActive = async (t) => {
    try {
      setSaving(true);
      await adminVehicleCatalogService.updateTrim(t._id, { isActive: !t.isActive });
      await fetchTrims(expandedModelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteMaker = async (m) => {
    if (!window.confirm(`Delete maker "${m.name}" and all of its models and variants?`)) return;
    try {
      setSaving(true);
      await adminVehicleCatalogService.deleteMaker(m._id);
      if (String(expandedMakerId) === String(m._id)) {
        setExpandedMakerId('');
        setExpandedModelId('');
      }
      toast.success('Maker deleted');
      await loadMakers();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteModel = async (m, makerId) => {
    if (!window.confirm(`Delete model "${m.name}" and all year/month/variant rows?`)) return;
    try {
      setSaving(true);
      await adminVehicleCatalogService.deleteModel(m._id);
      if (String(expandedModelId) === String(m._id)) setExpandedModelId('');
      toast.success('Model deleted');
      await fetchModels(makerId);
      if (editMakerModal && String(editMakerModal._id) === String(makerId)) {
        await loadModelsForEditModal(makerId);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteTrim = async (t) => {
    if (!window.confirm('Delete this catalogue row?')) return;
    try {
      setSaving(true);
      await adminVehicleCatalogService.deleteTrim(t._id);
      toast.success('Row deleted');
      await fetchTrims(expandedModelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const onAddCategory = async (e, modelId) => {
    e.preventDefault();
    const name = newCategory.name.trim();
    if (!name) {
      toast.error('Enter a category name');
      return;
    }
    try {
      setSaving(true);
      await adminVehicleCatalogService.createCategory(modelId, {
        name,
        description: newCategory.description.trim()
      });
      setNewCategory({ name: '', description: '' });
      toast.success('Category added');
      await fetchCategories(modelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not add category');
    } finally {
      setSaving(false);
    }
  };

  const saveCategoryEdit = async () => {
    if (!editingCategory) return;
    const name = (editingCategory.name || '').trim();
    if (!name) return;
    try {
      setSaving(true);
      await adminVehicleCatalogService.updateCategory(editingCategory._id, {
        name,
        description: (editingCategory.description || '').trim(),
        sortOrder:
          editingCategory.sortOrder !== '' && editingCategory.sortOrder != null
            ? Number(editingCategory.sortOrder)
            : 0
      });
      const mid = editingCategory.modelId || expandedModelCategoriesId;
      setEditingCategory(null);
      toast.success('Category updated');
      if (mid) await fetchCategories(mid);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteCatalogCategory = async (cat, modelId) => {
    if (!window.confirm(`Delete category "${cat.name}" and all spare parts in it?`)) return;
    try {
      setSaving(true);
      await adminVehicleCatalogService.deleteCategory(cat._id);
      if (String(expandedCategoryId) === String(cat._id)) setExpandedCategoryId('');
      toast.success('Category deleted');
      await fetchCategories(modelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategoryActive = async (cat, modelId) => {
    try {
      setSaving(true);
      await adminVehicleCatalogService.updateCategory(cat._id, { isActive: !cat.isActive });
      await fetchCategories(modelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategoryPartsView = (categoryId) => {
    if (expandedCategoryId === String(categoryId)) {
      setExpandedCategoryId('');
      return;
    }
    setExpandedCategoryId(String(categoryId));
  };

  const onAddPart = async (e, categoryId, modelId) => {
    e.preventDefault();
    const name = newPart.name.trim();
    if (!name) {
      toast.error('Enter a part name');
      return;
    }
    try {
      setSaving(true);
      await adminVehicleCatalogService.createPart(categoryId, {
        name,
        partNumber: newPart.partNumber.trim(),
        notes: newPart.notes.trim()
      });
      setNewPart({ name: '', partNumber: '', notes: '' });
      toast.success('Spare part added');
      await fetchParts(categoryId, modelId);
      await fetchCategories(modelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not add part');
    } finally {
      setSaving(false);
    }
  };

  const savePartEdit = async () => {
    if (!editingPart) return;
    const name = (editingPart.name || '').trim();
    if (!name) return;
    try {
      setSaving(true);
      const cid = editingPart.categoryId;
      const mid = editingPart.modelId;
      const allPartImageUrls = [
        editingPart.imageUrl || '',
        ...(Array.isArray(editingPart.imageUrls) ? editingPart.imageUrls : []),
        ...(Array.isArray(editingPart.images) ? editingPart.images : []),
      ]
        .map((u) => String(u || '').trim())
        .filter(Boolean);
      const imageUrls = [...new Set(allPartImageUrls)];
      if (editingPart.sourceType === 'product') {
        const productPayload = {
          name,
          partNumber: (editingPart.partNumber || '').trim(),
          description: (editingPart.notes || '').trim()
        };
        if (imageUrls.length) {
          productPayload.images = imageUrls;
          productPayload.imageUrls = imageUrls;
          productPayload.imageUrl = imageUrls[0];
        }
        await productService.updateAdminProduct(editingPart._id, productPayload);
      } else {
        const partPayload = {
          name,
          partNumber: (editingPart.partNumber || '').trim(),
          notes: (editingPart.notes || '').trim()
        };
        if (imageUrls.length) {
          partPayload.imageUrls = imageUrls;
          partPayload.imageUrl = imageUrls[0];
        }
        await adminVehicleCatalogService.updatePart(editingPart._id, partPayload);
      }
      setEditingPart(null);
      toast.success('Part updated');
      if (cid) await fetchParts(cid, mid);
      if (mid) await fetchModelProducts(mid);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteCatalogPart = async (part, categoryId, modelId) => {
    if (!window.confirm(`Delete spare part "${part.name}"?`)) return;
    try {
      setSaving(true);
      if (part.sourceType === 'product') {
        await productService.deleteAdminProduct(part._id);
      } else {
        await adminVehicleCatalogService.deletePart(part._id);
      }
      toast.success('Part deleted');
      await fetchParts(categoryId, modelId);
      if (modelId) await fetchModelProducts(modelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePartActive = async (part, categoryId, modelId) => {
    try {
      setSaving(true);
      if (part.sourceType === 'product') {
        await productService.updateAdminProduct(part._id, { isActive: !part.isActive });
      } else {
        await adminVehicleCatalogService.updatePart(part._id, { isActive: !part.isActive });
      }
      await fetchParts(categoryId, modelId);
      if (modelId) await fetchModelProducts(modelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const saveModelEdit = async () => {
    if (!editingModel) return;
    const name = (editingModel.name || '').trim();
    if (!name) return;
    const makerId = editingModel.makerId || expandedMakerId;
    const gyRaw = editingModel.generationYear;
    let generationYear;
    if (gyRaw === '' || gyRaw == null) {
      generationYear = null;
    } else {
      const gy = Number(gyRaw);
      if (!Number.isFinite(gy) || gy < 1980 || gy > 2100) {
        toast.error('Model year must be between 1980 and 2100, or leave empty.');
        return;
      }
      generationYear = gy;
    }
    try {
      setSaving(true);
      await adminVehicleCatalogService.updateModel(editingModel._id, {
        name,
        generation: (editingModel.generation || '').trim(),
        generationYear,
        variant: (editingModel.variant || '').trim(),
        fuelType: (editingModel.fuelType || '').trim(),
        transmissionType: (editingModel.transmissionType || '').trim(),
        productCategoryIds: Array.isArray(editingModel.productCategoryIds)
          ? editingModel.productCategoryIds.map(String)
          : []
      });
      setEditingModel(null);
      toast.success('Model updated');
      if (makerId) await fetchModels(makerId);
      if (editMakerModal && String(editMakerModal._id) === String(makerId)) {
        await loadModelsForEditModal(makerId);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const saveTrimEdit = async () => {
    if (!editingTrim) return;
    const generation = (editingTrim.generation || '').trim();
    const variant = (editingTrim.variant || '').trim();
    if (!generation) return;
    if (!variant) return;
    try {
      setSaving(true);
      await adminVehicleCatalogService.updateTrim(editingTrim._id, {
        generation,
        year: Number(editingTrim.year),
        month: Number(editingTrim.month),
        variant
      });
      setEditingTrim(null);
      toast.success('Row updated');
      await fetchTrims(expandedModelId);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const filteredMakers = useMemo(() => {
    let list = makers;
    const f = debouncedCatalogFilters;
    if (trimStr(f.makerId)) {
      list = list.filter((m) => String(m._id) === String(f.makerId));
    }
    if (f.status === 'active') list = list.filter((m) => m.isActive);
    if (f.status === 'inactive') list = list.filter((m) => !m.isActive);
    if (!needsDeepCatalogData) return list;
    return list.filter((m) => {
      const models = modelsByMaker[m._id] || [];
      return models.some((mod) =>
        modelMatchesCatalogFilters(mod, f, trimsByModel[mod._id])
      );
    });
  }, [makers, modelsByMaker, trimsByModel, debouncedCatalogFilters, needsDeepCatalogData]);

  const modelsForExpandedMaker = useMemo(() => {
    if (!expandedMakerId) return [];
    const models = modelsByMaker[expandedMakerId] || [];
    const f = debouncedCatalogFilters;
    if (!needsDeepCatalogData) return models;
    return models.filter((mod) =>
      modelMatchesCatalogFilters(mod, f, trimsByModel[mod._id])
    );
  }, [
    expandedMakerId,
    modelsByMaker,
    trimsByModel,
    debouncedCatalogFilters,
    needsDeepCatalogData
  ]);

  useEffect(() => {
    if (!expandedMakerId) return;
    if (!filteredMakers.some((m) => String(m._id) === String(expandedMakerId))) {
      setExpandedMakerId('');
      setExpandedModelId('');
      setExpandedModelCategoriesId('');
    }
  }, [filteredMakers, expandedMakerId]);

  useEffect(() => {
    if (!expandedModelId) return;
    if (!modelsForExpandedMaker.some((mod) => String(mod._id) === String(expandedModelId))) {
      setExpandedModelId('');
    }
  }, [modelsForExpandedMaker, expandedModelId]);

  useEffect(() => {
    if (!expandedModelCategoriesId) return;
    if (
      !modelsForExpandedMaker.some((mod) => String(mod._id) === String(expandedModelCategoriesId))
    ) {
      setExpandedModelCategoriesId('');
    }
  }, [modelsForExpandedMaker, expandedModelCategoriesId]);

  const actionBtn =
    'inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-primary-700 transition disabled:opacity-50';

  const startEditModel = (mod, makerId) => {
    setExpandedModelCategoriesId('');
    setEditingModel({
      ...mod,
      makerId,
      generation: mod.generation ?? '',
      generationYear: mod.generationYear != null ? String(mod.generationYear) : '',
      variant: mod.variant ?? '',
      fuelType: mod.fuelType ?? '',
      transmissionType: mod.transmissionType ?? '',
      productCategoryIds: modelProductCategoryIdsFromApi(mod)
    });
  };

  const dash = (v) => (v != null && String(v).trim() !== '' ? v : '—');

  return (
    <div className="space-y-6">
      {viewPartModal && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="view-part-details-title"
          onClick={() => setViewPartModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 id="view-part-details-title" className="text-base font-bold text-gray-900">
                Spare part details
              </h3>
              <button
                type="button"
                onClick={() => setViewPartModal(null)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase">Part</p>
                <p className="text-gray-900 font-semibold">{dash(viewPartModal.name)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase">Part #</p>
                  <p className="text-gray-800">{dash(viewPartModal.partNumber)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase">Status</p>
                  <p className="text-gray-800">{viewPartModal.isActive ? 'Active' : 'Off'}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase">Model</p>
                <p className="text-gray-800">{dash(viewPartModal.modelName)}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase">Category</p>
                <p className="text-gray-800">{dash(viewPartModal.categoryName)}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase">Notes</p>
                <p className="text-gray-800">{dash(viewPartModal.notes)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!hideTitle && (
        <div>
          <h2
            className={`font-bold text-gray-900 flex items-center gap-2 ${
              embedded ? 'text-base' : 'text-lg'
            }`}
          >
            <FaLayerGroup className="text-primary-600" />
            Master catalogue
          </h2>
          {embedded ? (
            <p className="text-xs text-gray-600 mt-1 max-w-3xl">
              Use the <span className="font-semibold">chevron</span> to expand a maker and see all
              models. <span className="font-semibold">Eye</span> opens model details;{' '}
              <span className="font-semibold">layers</span> expands year / month / variant rows. Use{' '}
              <span className="font-semibold">Add vehicle</span> above for a full catalogue row in one
              step.
            </p>
          ) : (
            <p className="text-xs text-gray-600 mt-1 max-w-3xl">
              Maintain the master list: one car maker can have many models; each model can have many
              rows of model year, month, and variant (trim). Customer-facing pages can read the public
              API under{' '}
              <code className="text-[11px] bg-gray-100 px-1 rounded">/api/vehicle-catalog/…</code>
              ; admin changes use authenticated{' '}
              <code className="text-[11px] bg-gray-100 px-1 rounded">/api/admin/vehicle-catalog/…</code>
              .
            </p>
          )}
        </div>
      )}

      {viewModelModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="view-model-details-title"
          onClick={closeViewModelDetails}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div>
                <h3 id="view-model-details-title" className="text-base font-bold text-gray-900">
                  Model details
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {viewModelModal.maker?.name ? (
                    <>
                      <span className="font-semibold text-gray-700">{viewModelModal.maker.name}</span>
                      <span className="mx-1">·</span>
                    </>
                  ) : null}
                  <span className="font-medium text-gray-800">{viewModelModal.model?.name}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={closeViewModelDetails}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {Array.isArray(viewModelModal.model?.imageUrls) &&
                viewModelModal.model.imageUrls.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Images</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {viewModelModal.model.imageUrls.map((url) => (
                        <div
                          key={url}
                          className="aspect-[4/3] rounded-xl border border-gray-200 bg-gray-50 overflow-hidden"
                        >
                          <img
                            src={url}
                            alt={`${viewModelModal.model.name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div>
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Car maker</dt>
                  <dd className="text-gray-900 mt-0.5">{dash(viewModelModal.maker?.name)}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Model name</dt>
                  <dd className="text-gray-900 mt-0.5">{dash(viewModelModal.model?.name)}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Generation</dt>
                  <dd className="text-gray-900 mt-0.5">{dash(viewModelModal.model?.generation)}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Model year</dt>
                  <dd className="text-gray-900 mt-0.5 tabular-nums">
                    {dash(viewModelModal.model?.generationYear)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Variant line</dt>
                  <dd className="text-gray-900 mt-0.5">{dash(viewModelModal.model?.variant)}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Fuel</dt>
                  <dd className="text-gray-900 mt-0.5">{dash(viewModelModal.model?.fuelType)}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Transmission</dt>
                  <dd className="text-gray-900 mt-0.5">
                    {dash(viewModelModal.model?.transmissionType)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Status</dt>
                  <dd className="mt-0.5">
                    <span
                      className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                        viewModelModal.model?.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {viewModelModal.model?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-[10px] font-semibold text-gray-500 uppercase">Catalogue model ID</dt>
                  <dd className="text-gray-600 font-mono text-xs mt-0.5 break-all">
                    {String(viewModelModal.model?._id || '')}
                  </dd>
                </div>
                {(viewModelModal.model?.createdAt || viewModelModal.model?.updatedAt) && (
                  <div className="sm:col-span-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                    {viewModelModal.model?.createdAt && (
                      <span>Created: {formatShortDate(viewModelModal.model.createdAt)}</span>
                    )}
                    {viewModelModal.model?.updatedAt && (
                      <span>Updated: {formatShortDate(viewModelModal.model.updatedAt)}</span>
                    )}
                  </div>
                )}
              </dl>

              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">
                  Product categories
                </p>
                {modelProductCategoryIdsFromApi(viewModelModal.model).length === 0 ? (
                  <p className="text-sm text-gray-500">— None assigned</p>
                ) : (
                  <ul className="flex flex-wrap gap-1.5">
                    {modelProductCategoryIdsFromApi(viewModelModal.model).map((cid) => (
                      <li
                        key={cid}
                        className="text-xs px-2 py-1 rounded-lg bg-primary-50 text-primary-900 border border-primary-100"
                      >
                        {productCategoryLabel(cid)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">
                  Trim lines (year / month / variant)
                </p>
                {loadingTrimsFor[viewModelModal.model._id] ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                    <FaSpinner className="animate-spin" />
                    Loading trims…
                  </div>
                ) : (trimsByModel[viewModelModal.model._id] || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No trim rows yet.</p>
                ) : (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Generation</th>
                          <th className="text-left px-3 py-2 font-semibold">Year</th>
                          <th className="text-left px-3 py-2 font-semibold">Month</th>
                          <th className="text-left px-3 py-2 font-semibold">Variant</th>
                          <th className="text-left px-3 py-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(trimsByModel[viewModelModal.model._id] || []).map((t) => (
                          <tr key={t._id || `${t.year}-${t.month}-${t.variant}`} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-800">{dash(t.generation)}</td>
                            <td className="px-3 py-2 tabular-nums">{dash(t.year)}</td>
                            <td className="px-3 py-2">
                              {t.month != null && MONTHS[t.month - 1]
                                ? MONTHS[t.month - 1].label
                                : dash(t.month)}
                            </td>
                            <td className="px-3 py-2 text-gray-800">{dash(t.variant)}</td>
                            <td className="px-3 py-2">
                              <span
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                  t.isActive !== false
                                    ? 'bg-green-50 text-green-800'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {t.isActive !== false ? 'Active' : 'Off'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    const mid = viewModelModal.model._id;
                    closeViewModelDetails();
                    toggleModelView(mid);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-200 bg-primary-50 text-primary-900 text-sm font-semibold hover:bg-primary-100"
                >
                  <FaLayerGroup className="text-sm" />
                  Manage trim lines
                </button>
                <button
                  type="button"
                  onClick={closeViewModelDetails}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {addModelModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => !saving && setAddModelModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Add model</h3>
              <button
                type="button"
                onClick={() => !saving && setAddModelModal(null)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={submitAddModelModal} className="p-5 space-y-3">
              <p className="text-xs text-gray-600">
                Add a model under <span className="font-semibold">{addModelModal.makerName}</span>.
                Generation, model year, variant line, and fuel are optional but recommended. Select one
                or more product categories at the bottom (from superadmin Categories).
              </p>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Model name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  autoFocus
                  value={addModelForm.name}
                  onChange={(e) => setAddModelForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. City, Swift"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Generation
                </label>
                <input
                  type="text"
                  value={addModelForm.generation}
                  onChange={(e) => setAddModelForm((f) => ({ ...f, generation: e.target.value }))}
                  placeholder="e.g. 5th gen, Mk8"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Model year
                  </label>
                  <input
                    type="number"
                    min={1980}
                    max={2100}
                    value={addModelForm.generationYear}
                    onChange={(e) =>
                      setAddModelForm((f) => ({ ...f, generationYear: e.target.value }))
                    }
                    placeholder="e.g. 2022"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Fuel type
                  </label>
                  <select
                    value={addModelForm.fuelType}
                    onChange={(e) => setAddModelForm((f) => ({ ...f, fuelType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {FUEL_OPTIONS.map((opt) => (
                      <option key={opt || 'any'} value={opt}>
                        {opt || '— Select —'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Type
                  </label>
                  <select
                    value={addModelForm.transmissionType}
                    onChange={(e) =>
                      setAddModelForm((f) => ({ ...f, transmissionType: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {TRANSMISSION_OPTIONS.map((opt) => (
                      <option key={opt || 'none'} value={opt}>
                        {opt || '— Select —'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Variant (model line)
                </label>
                <input
                  type="text"
                  value={addModelForm.variant}
                  onChange={(e) => setAddModelForm((f) => ({ ...f, variant: e.target.value }))}
                  placeholder="e.g. VX, ZXi — distinct from per-trim catalogue rows below"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Model images (optional)
                </label>
                <label className="inline-flex items-center gap-2 text-xs text-primary-700 cursor-pointer hover:text-primary-800">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={saving || uploadingAddModelImages}
                    onChange={async (e) => {
                      await uploadModelImages(e.target.files, 'add');
                      e.target.value = '';
                    }}
                  />
                  {uploadingAddModelImages ? 'Uploading images…' : 'Upload image files'}
                </label>
                {!!(addModelForm.imageUrls || []).length && (
                  <div className="mt-2 space-y-1 max-h-24 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {(addModelForm.imageUrls || []).map((url) => (
                      <div key={url} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="truncate text-gray-700">{url}</span>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700 shrink-0"
                          onClick={() =>
                            setAddModelForm((f) => ({
                              ...f,
                              imageUrls: (f.imageUrls || []).filter((u) => u !== url)
                            }))
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="pt-1 border-t border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase">
                    Product categories
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate(ADMIN_CATEGORIES_PANEL_PATH)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 hover:text-primary-800"
                  >
                    <FaPlus className="text-[10px]" />
                    Create category
                    <FaExternalLinkAlt className="text-[10px] opacity-80" aria-hidden />
                  </button>
                </div>
                <p className="text-[10px] text-gray-500 mb-2">
                  Select any number (e.g. two or more). Same list as superadmin{' '}
                  <span className="font-medium">Categories</span>.
                </p>
                <div className="border border-gray-200 rounded-lg p-2 max-h-44 overflow-y-auto space-y-1.5 bg-gray-50/50">
                  {sortedProductCategories.length === 0 ? (
                    <p className="text-xs text-gray-500 py-1">
                      No categories loaded. Add them under Categories in the admin panel.
                    </p>
                  ) : (
                    sortedProductCategories.map((cat) => {
                      const cid = String(cat._id || cat.id || '');
                      if (!cid) return null;
                      const nm = cat.name ?? cat;
                      const checked = (addModelForm.productCategoryIds || []).map(String).includes(cid);
                      return (
                        <label
                          key={cid}
                          className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer hover:bg-white/90 rounded px-1.5 py-0.5"
                        >
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            checked={checked}
                            onChange={(e) =>
                              setAddModelForm((f) => ({
                                ...f,
                                productCategoryIds: toggleCategoryIdInList(
                                  f.productCategoryIds,
                                  cid,
                                  e.target.checked
                                )
                              }))
                            }
                          />
                          <span>{nm}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setAddModelModal(null)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? <FaSpinner className="animate-spin" /> : <FaPlus className="text-xs" />}
                  Add model
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editMakerModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => !saving && setEditMakerModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Edit car maker</h3>
              <button
                type="button"
                onClick={() => !saving && setEditMakerModal(null)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  Maker name
                </label>
                <input
                  type="text"
                  value={editMakerName}
                  onChange={(e) => setEditMakerName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveMakerFromModal}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                >
                  Save maker
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setEditMakerModal(null)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>

              <div className="border-t border-gray-100 pt-5 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-bold text-gray-900">Models under this maker</h4>
                  <button
                    type="button"
                    onClick={() => setShowAddModelInEdit((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-800"
                  >
                    <FaPlus className="text-[10px]" />
                    Add model
                  </button>
                </div>
                {showAddModelInEdit && (
                  <form onSubmit={addModelFromEditModal} className="space-y-3 border border-gray-100 rounded-xl p-3 bg-gray-50/80">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase">New model</p>
                    <input
                      type="text"
                      value={newModelDraft.name}
                      onChange={(e) => setNewModelDraft((d) => ({ ...d, name: e.target.value }))}
                      placeholder="Model name *"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      value={newModelDraft.generation}
                      onChange={(e) =>
                        setNewModelDraft((d) => ({ ...d, generation: e.target.value }))
                      }
                      placeholder="Generation (optional)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        min={1980}
                        max={2100}
                        value={newModelDraft.generationYear}
                        onChange={(e) =>
                          setNewModelDraft((d) => ({ ...d, generationYear: e.target.value }))
                        }
                        placeholder="Model year"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <select
                        value={newModelDraft.fuelType}
                        onChange={(e) =>
                          setNewModelDraft((d) => ({ ...d, fuelType: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        {FUEL_OPTIONS.map((opt) => (
                          <option key={opt || 'any'} value={opt}>
                            {opt || 'Fuel type'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <select
                        value={newModelDraft.transmissionType}
                        onChange={(e) =>
                          setNewModelDraft((d) => ({ ...d, transmissionType: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        {TRANSMISSION_OPTIONS.map((opt) => (
                          <option key={opt || 'none'} value={opt}>
                            {opt || 'Type (Automatic / Manual)'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="text"
                      value={newModelDraft.variant}
                      onChange={(e) => setNewModelDraft((d) => ({ ...d, variant: e.target.value }))}
                      placeholder="Variant line (optional)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
                        Model images (optional)
                      </label>
                      <label className="inline-flex items-center gap-2 text-xs text-primary-700 cursor-pointer hover:text-primary-800">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          disabled={saving || uploadingEditModelImages}
                          onChange={async (e) => {
                            await uploadModelImages(e.target.files, 'edit');
                            e.target.value = '';
                          }}
                        />
                        {uploadingEditModelImages ? 'Uploading images…' : 'Upload image files'}
                      </label>
                      {!!(newModelDraft.imageUrls || []).length && (
                        <div className="mt-2 space-y-1 max-h-20 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white">
                          {(newModelDraft.imageUrls || []).map((url) => (
                            <div key={url} className="flex items-center justify-between gap-2 text-[11px]">
                              <span className="truncate text-gray-700">{url}</span>
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-700 shrink-0"
                                onClick={() =>
                                  setNewModelDraft((d) => ({
                                    ...d,
                                    imageUrls: (d.imageUrls || []).filter((u) => u !== url)
                                  }))
                                }
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="pt-1 border-t border-gray-200">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase">
                          Product categories
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate(ADMIN_CATEGORIES_PANEL_PATH)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary-700 hover:text-primary-800"
                        >
                          <FaPlus className="text-[9px]" />
                          Create category
                          <FaExternalLinkAlt className="text-[9px] opacity-80" aria-hidden />
                        </button>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-2 max-h-36 overflow-y-auto space-y-1 bg-white">
                        {sortedProductCategories.length === 0 ? (
                          <p className="text-[10px] text-gray-500">No categories available.</p>
                        ) : (
                          sortedProductCategories.map((cat) => {
                            const cid = String(cat._id || cat.id || '');
                            if (!cid) return null;
                            const nm = cat.name ?? cat;
                            const checked = (newModelDraft.productCategoryIds || [])
                              .map(String)
                              .includes(cid);
                            return (
                              <label
                                key={cid}
                                className="flex items-center gap-2 text-xs text-gray-800 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300 text-primary-600"
                                  checked={checked}
                                  onChange={(e) =>
                                    setNewModelDraft((d) => ({
                                      ...d,
                                      productCategoryIds: toggleCategoryIdInList(
                                        d.productCategoryIds,
                                        cid,
                                        e.target.checked
                                      )
                                    }))
                                  }
                                />
                                <span>{nm}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={saving || !newModelDraft.name.trim()}
                      className="w-full px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                    >
                      Add model
                    </button>
                  </form>
                )}
                {loadingEditModalModels ? (
                  <div className="flex justify-center py-6">
                    <FaSpinner className="animate-spin text-primary-600 text-xl" />
                  </div>
                ) : (
                  <ul className="border border-gray-100 rounded-xl divide-y divide-gray-100 max-h-56 overflow-y-auto text-sm">
                    {editModalModels.map((mod) => (
                      <li
                        key={mod._id}
                        className="px-3 py-2.5 flex items-center justify-between gap-2"
                      >
                        <span className="font-medium text-gray-900 min-w-0">
                          <span className="block truncate">{mod.name}</span>
                          <span className="block text-[11px] text-gray-500 font-normal truncate">
                            {[mod.generation, mod.generationYear, mod.variant, mod.fuelType, mod.transmissionType]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </span>
                          {!mod.isActive && (
                            <span className="text-[10px] uppercase text-gray-400">inactive</span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => deleteModelFromEditModal(mod)}
                          className="text-red-600 p-1.5 rounded-lg hover:bg-red-50 shrink-0"
                          title="Delete model"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </li>
                    ))}
                    {!editModalModels.length && (
                      <li className="px-3 py-6 text-center text-xs text-gray-500">
                        No models yet. Use Add model above.
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!hideInlineAddForms && (
        <form onSubmit={onAddMaker} className="flex flex-col sm:flex-row gap-2 max-w-xl">
          <input
            type="text"
            placeholder="New car maker name (e.g. Toyota)"
            value={newMakerName}
            onChange={(e) => setNewMakerName(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 shrink-0"
          >
            <FaPlus className="text-xs" />
            Add maker
          </button>
        </form>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
            <FaFilter className="text-primary-600 text-sm" />
            Filters
          </div>
          <div className="flex items-center gap-2">
            {filterIndexLoading && needsDeepCatalogData && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <FaSpinner className="animate-spin text-primary-600" />
                Loading catalogue for filters…
              </span>
            )}
            <button
              type="button"
              onClick={() => setCatalogFilters({ ...EMPTY_CATALOG_FILTERS })}
              className="text-xs font-semibold text-primary-700 hover:text-primary-800 disabled:opacity-50"
              disabled={Object.values(catalogFilters).every((v) => trimStr(v) === '')}
            >
              Clear all
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Maker
            </label>
            <select
              value={catalogFilters.makerId}
              onChange={(e) =>
                setCatalogFilters((f) => ({ ...f, makerId: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="">All makers</option>
              {makers.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Status
            </label>
            <select
              value={catalogFilters.status}
              onChange={(e) =>
                setCatalogFilters((f) => ({ ...f, status: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Model
            </label>
            <input
              type="text"
              value={catalogFilters.model}
              onChange={(e) =>
                setCatalogFilters((f) => ({ ...f, model: e.target.value }))
              }
              placeholder="Contains…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Generation
            </label>
            <input
              type="text"
              value={catalogFilters.generation}
              onChange={(e) =>
                setCatalogFilters((f) => ({ ...f, generation: e.target.value }))
              }
              placeholder="Contains…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Year
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={catalogFilters.year}
              onChange={(e) =>
                setCatalogFilters((f) => ({ ...f, year: e.target.value }))
              }
              placeholder="Model or trim year"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Fuel
            </label>
            <select
              value={catalogFilters.fuel}
              onChange={(e) =>
                setCatalogFilters((f) => ({ ...f, fuel: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              {FUEL_OPTIONS.map((opt) => (
                <option key={opt || 'any-fuel'} value={opt}>
                  {opt || 'All fuels'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Variant
            </label>
            <input
              type="text"
              value={catalogFilters.variant}
              onChange={(e) =>
                setCatalogFilters((f) => ({ ...f, variant: e.target.value }))
              }
              placeholder="Model line or trim"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        {(trimStr(catalogFilters.makerId) ||
          trimStr(catalogFilters.status) ||
          trimStr(catalogFilters.model) ||
          trimStr(catalogFilters.generation) ||
          trimStr(catalogFilters.year) ||
          trimStr(catalogFilters.fuel) ||
          trimStr(catalogFilters.variant)) && (
          <p className="text-xs text-gray-500">
            Showing <span className="font-semibold text-gray-700">{filteredMakers.length}</span> of{' '}
            <span className="font-semibold text-gray-700">{makers.length}</span> makers
            {needsDeepCatalogData && ' (match any model / trim in that maker)'}
            .
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative">
        {filterIndexLoading && needsDeepCatalogData && (
          <div
            className="absolute inset-0 z-10 bg-white/60 flex items-center justify-center"
            aria-busy="true"
            aria-label="Loading filter data"
          >
            <FaSpinner className="animate-spin text-primary-600 text-2xl" />
          </div>
        )}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Car makers</h3>
          {loadingMakers && <FaSpinner className="animate-spin text-primary-600" />}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Maker</th>
                <th className="px-4 py-3 w-28">Status</th>
                <th className="px-4 py-3 w-24 text-center">Models</th>
                <th className="px-4 py-3 w-28 whitespace-nowrap">Added</th>
                <th className="px-4 py-3 text-right min-w-[180px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMakers.map((m) => {
                const isOpen = String(expandedMakerId) === String(m._id);
                const modelList = modelsByMaker[m._id];
                const modelCountLoading =
                  !Array.isArray(modelList) && Boolean(loadingModelsFor[String(m._id)]);
                let modelCount;
                if (!Array.isArray(modelList)) {
                  modelCount = undefined;
                } else if (needsDeepCatalogData) {
                  modelCount = modelList.filter((mod) =>
                    modelMatchesCatalogFilters(
                      mod,
                      debouncedCatalogFilters,
                      trimsByModel[mod._id]
                    )
                  ).length;
                } else {
                  modelCount = modelList.length;
                }
                return (
                <React.Fragment key={m._id}>
                  <tr className={!m.isActive ? 'bg-gray-50/80' : ''}>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          title={isOpen ? 'Collapse models' : 'Expand models'}
                          aria-expanded={isOpen}
                          onClick={() => toggleMakerView(m._id)}
                          className="mt-0.5 p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-primary-700 shrink-0"
                        >
                          <FaChevronDown
                            className={`text-sm transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <FaCar className="text-yellow-600 shrink-0 text-xs" />
                            <span className="font-semibold text-gray-900">{m.name}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!m.isActive}
                          aria-label={
                            m.isActive
                              ? 'Active — click to deactivate this maker'
                              : 'Inactive — click to activate this maker'
                          }
                          disabled={saving}
                          onClick={(e) => toggleMakerActive(m, e)}
                          className={`inline-flex h-7 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent px-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 items-center ${
                            m.isActive ? 'justify-end bg-green-500' : 'justify-start bg-gray-300'
                          } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out" />
                        </button>
                        <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                          {m.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700 tabular-nums">
                      {modelCountLoading ? (
                        <span className="inline-flex items-center justify-center">
                          <FaSpinner className="animate-spin text-primary-600 text-xs" />
                        </span>
                      ) : (
                        modelCount ?? 0
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {formatShortDate(m.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <button
                          type="button"
                          title="Add model"
                          onClick={(e) => {
                            e.stopPropagation();
                            openAddModelModal(m);
                          }}
                          className={actionBtn}
                        >
                          <FaPlus className="text-sm" />
                        </button>
                        <button
                          type="button"
                          title="Edit maker"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditMakerModal(m);
                          }}
                          className={actionBtn}
                        >
                          <FaEdit className="text-sm" />
                        </button>
                        <button
                          type="button"
                          title="Delete maker"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMaker(m);
                          }}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-red-100 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} className="bg-gray-50/50 p-0">
                        <div className="px-4 py-4 border-t border-gray-100">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Models — {m.name}
                            </p>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openAddModelModal(m)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700"
                              >
                                <FaPlus className="text-[10px]" />
                                Add model
                              </button>
                              {loadingModelsFor[m._id] && (
                                <FaSpinner className="animate-spin text-primary-600 text-sm" />
                              )}
                            </div>
                          </div>
                          {!modelsForExpandedMaker.length && !loadingModelsFor[m._id] ? (
                            <p className="text-xs text-gray-500 py-2">
                              No models yet. Use <span className="font-semibold">Add model</span> above
                              or the + action on the maker row.
                            </p>
                          ) : (
                            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-600">
                                      Model
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-600 min-w-[7rem]">
                                      Categories
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-600">
                                      Generation
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-600 whitespace-nowrap">
                                      Model year
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-600">
                                      Variant
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-600">
                                      Fuel
                                    </th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-600 w-24">
                                      Status
                                    </th>
                                    <th className="text-right px-3 py-2 font-semibold text-gray-600 w-[180px]">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {modelsForExpandedMaker.map((mod) => {
                                    const categoryCount = modelProductCategoryIdsFromApi(mod).length;
                                    return (
                                    <React.Fragment key={mod._id}>
                                      {editingModel &&
                                      String(editingModel._id) === String(mod._id) ? (
                                        <tr className="bg-amber-50/50">
                                          <td colSpan={8} className="px-3 py-3">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                              <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                  Model name
                                                </label>
                                                <input
                                                  type="text"
                                                  value={editingModel.name}
                                                  onChange={(e) =>
                                                    setEditingModel({
                                                      ...editingModel,
                                                      name: e.target.value
                                                    })
                                                  }
                                                  className="w-full px-2 py-1.5 border rounded-lg text-xs mt-0.5"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                  Generation
                                                </label>
                                                <input
                                                  type="text"
                                                  value={editingModel.generation}
                                                  onChange={(e) =>
                                                    setEditingModel({
                                                      ...editingModel,
                                                      generation: e.target.value
                                                    })
                                                  }
                                                  className="w-full px-2 py-1.5 border rounded-lg text-xs mt-0.5"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                  Model year
                                                </label>
                                                <input
                                                  type="number"
                                                  min={1980}
                                                  max={2100}
                                                  value={editingModel.generationYear}
                                                  onChange={(e) =>
                                                    setEditingModel({
                                                      ...editingModel,
                                                      generationYear: e.target.value
                                                    })
                                                  }
                                                  placeholder="Optional"
                                                  className="w-full px-2 py-1.5 border rounded-lg text-xs mt-0.5"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                  Variant (line)
                                                </label>
                                                <input
                                                  type="text"
                                                  value={editingModel.variant}
                                                  onChange={(e) =>
                                                    setEditingModel({
                                                      ...editingModel,
                                                      variant: e.target.value
                                                    })
                                                  }
                                                  className="w-full px-2 py-1.5 border rounded-lg text-xs mt-0.5"
                                                />
                                              </div>
                                              <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                  Fuel type
                                                </label>
                                                <select
                                                  value={editingModel.fuelType}
                                                  onChange={(e) =>
                                                    setEditingModel({
                                                      ...editingModel,
                                                      fuelType: e.target.value
                                                    })
                                                  }
                                                  className="w-full px-2 py-1.5 border rounded-lg text-xs mt-0.5"
                                                >
                                                  {FUEL_OPTIONS.map((opt) => (
                                                    <option key={opt || 'any'} value={opt}>
                                                      {opt || '— None —'}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div>
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                  Type
                                                </label>
                                                <select
                                                  value={editingModel.transmissionType || ''}
                                                  onChange={(e) =>
                                                    setEditingModel({
                                                      ...editingModel,
                                                      transmissionType: e.target.value
                                                    })
                                                  }
                                                  className="w-full px-2 py-1.5 border rounded-lg text-xs mt-0.5"
                                                >
                                                  {TRANSMISSION_OPTIONS.map((opt) => (
                                                    <option key={opt || 'none'} value={opt}>
                                                      {opt || '— None —'}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            </div>
                                            <div className="mt-3 pt-3 border-t border-amber-200/80">
                                              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                                <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                  Product categories
                                                </label>
                                                <button
                                                  type="button"
                                                  onClick={() => navigate(ADMIN_CATEGORIES_PANEL_PATH)}
                                                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary-700 hover:text-primary-800"
                                                >
                                                  <FaPlus className="text-[9px]" />
                                                  Create category
                                                  <FaExternalLinkAlt className="text-[9px] opacity-80" aria-hidden />
                                                </button>
                                              </div>
                                              <p className="text-[10px] text-gray-500 mb-2">
                                                Select any number (e.g. two or more). From superadmin
                                                Categories.
                                              </p>
                                              <div className="border border-gray-200 rounded-lg p-2 max-h-36 overflow-y-auto space-y-1 bg-white">
                                                {sortedProductCategories.length === 0 ? (
                                                  <p className="text-[10px] text-gray-500">
                                                    No categories loaded.
                                                  </p>
                                                ) : (
                                                  sortedProductCategories.map((cat) => {
                                                    const cid = String(cat._id || cat.id || '');
                                                    if (!cid) return null;
                                                    const nm = cat.name ?? cat;
                                                    const checked = (
                                                      editingModel.productCategoryIds || []
                                                    )
                                                      .map(String)
                                                      .includes(cid);
                                                    return (
                                                      <label
                                                        key={cid}
                                                        className="flex items-center gap-2 text-xs text-gray-800 cursor-pointer"
                                                      >
                                                        <input
                                                          type="checkbox"
                                                          className="rounded border-gray-300 text-primary-600"
                                                          checked={checked}
                                                          onChange={(e) =>
                                                            setEditingModel({
                                                              ...editingModel,
                                                              productCategoryIds: toggleCategoryIdInList(
                                                                editingModel.productCategoryIds,
                                                                cid,
                                                                e.target.checked
                                                              )
                                                            })
                                                          }
                                                        />
                                                        <span>{nm}</span>
                                                      </label>
                                                    );
                                                  })
                                                )}
                                              </div>
                                            </div>
                                            <div className="flex gap-2 mt-3">
                                              <button
                                                type="button"
                                                onClick={saveModelEdit}
                                                disabled={saving}
                                                className="text-xs font-semibold text-white bg-primary-600 px-3 py-1.5 rounded-lg"
                                              >
                                                Save model
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => setEditingModel(null)}
                                                className="text-xs text-gray-600 px-3 py-1.5"
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      ) : (
                                        <>
                                        <tr className={!mod.isActive ? 'opacity-70' : ''}>
                                          <td className="px-3 py-2.5">
                                            <div className="flex items-start gap-2">
                                              <button
                                                type="button"
                                                title={
                                                  expandedModelCategoriesId === String(mod._id)
                                                    ? 'Hide categories'
                                                    : 'Show categories & spare parts'
                                                }
                                                aria-expanded={
                                                  expandedModelCategoriesId === String(mod._id)
                                                }
                                                onClick={() => toggleModelCategoriesView(mod._id)}
                                                className="mt-0.5 p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-primary-700 shrink-0"
                                              >
                                                <FaChevronDown
                                                  className={`text-sm transition-transform duration-200 ${
                                                    expandedModelCategoriesId === String(mod._id)
                                                      ? 'rotate-180'
                                                      : ''
                                                  }`}
                                                />
                                              </button>
                                              <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                  <FaCar className="text-yellow-600 shrink-0 text-xs" />
                                                  <span className="font-semibold text-gray-900">
                                                    {mod.name}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </td>
                                          <td className="px-3 py-2.5 text-gray-700 max-w-[10rem]">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-[10px] font-semibold text-gray-700">
                                              {categoryCount} categor{categoryCount === 1 ? 'y' : 'ies'}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2.5 text-gray-700">
                                            {dash(mod.generation)}
                                          </td>
                                          <td className="px-3 py-2.5 text-gray-700 tabular-nums">
                                            {dash(mod.generationYear)}
                                          </td>
                                          <td className="px-3 py-2.5 text-gray-700">
                                            {dash(mod.variant)}
                                          </td>
                                          <td className="px-3 py-2.5 text-gray-700">
                                            {dash(mod.fuelType)}
                                          </td>
                                          <td className="px-3 py-2.5">
                                            <button
                                              type="button"
                                              onClick={() => toggleModelActive(mod, m._id)}
                                              disabled={saving}
                                              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                                mod.isActive
                                                  ? 'bg-green-100 text-green-800'
                                                  : 'bg-gray-200 text-gray-600'
                                              }`}
                                            >
                                              {mod.isActive ? 'Active' : 'Off'}
                                            </button>
                                          </td>
                                          <td className="px-3 py-2.5 whitespace-nowrap">
                                            <div className="inline-flex items-center justify-end gap-1 whitespace-nowrap">
                                              <button
                                                type="button"
                                                title="View model details"
                                                onClick={() => openViewModelDetails(mod, m)}
                                                className={`${actionBtn} w-7 h-7`}
                                              >
                                                <FaEye className="text-xs" />
                                              </button>
                                              <button
                                                type="button"
                                                title="Manage year / month / variant rows"
                                                onClick={() => toggleModelView(mod._id)}
                                                className={`${actionBtn} w-7 h-7 ${
                                                  String(expandedModelId) === String(mod._id)
                                                    ? 'bg-primary-50 border-primary-200 text-primary-800'
                                                    : ''
                                                }`}
                                              >
                                                <FaLayerGroup className="text-xs" />
                                              </button>
                                              <button
                                                type="button"
                                                title="Edit model"
                                                onClick={() => startEditModel(mod, m._id)}
                                                className={`${actionBtn} w-7 h-7`}
                                              >
                                                <FaEdit className="text-xs" />
                                              </button>
                                              <button
                                                type="button"
                                                title="Delete model"
                                                onClick={() => deleteModel(mod, m._id)}
                                                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-red-100 text-red-600 hover:bg-red-50"
                                              >
                                                <FaTrash className="text-xs" />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      {String(expandedModelCategoriesId) === String(mod._id) && (
                                        <tr>
                                          <td colSpan={8} className="bg-gray-50/70 p-0">
                                            <div className="px-3 pb-4 pt-3 border-t border-gray-200">
                                              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                                                <div className="px-3 py-2.5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
                                                  <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                                                    Spare parts - {mod.name}
                                                  </p>
                                                  <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                    {(categoriesByModel[mod._id] || []).length} categor
                                                    {(categoriesByModel[mod._id] || []).length === 1
                                                      ? 'y'
                                                      : 'ies'}
                                                  </span>
                                                </div>
                                                <div className="p-3">
                                                  {loadingCategoriesFor[mod._id] ? (
                                                    <div className="flex justify-center py-4">
                                                      <FaSpinner className="animate-spin text-primary-600" />
                                                    </div>
                                                  ) : !(categoriesByModel[mod._id] || []).length ? (
                                                    <p className="text-xs text-gray-500">
                                                      No spare-part categories mapped to this model yet.
                                                    </p>
                                                  ) : (
                                                    <div className="space-y-3">
                                                      {(categoriesByModel[mod._id] || []).map((cat) => {
                                                        const categoryId = String(cat?._id || cat?.id || '');
                                                        const categoryPartsKey = `${String(mod._id)}::${categoryId}`;
                                                        const parts = categoryId
                                                          ? partsByCategory[categoryPartsKey] || []
                                                          : [];
                                                        const loadingParts = categoryId
                                                          ? loadingPartsFor[categoryPartsKey]
                                                          : false;
                                                        return (
                                                          <div
                                                            key={categoryId || cat.name}
                                                            className="rounded-lg border border-gray-200 bg-white"
                                                          >
                                                            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
                                                              <p className="text-xs font-semibold text-gray-800">
                                                                {dash(cat.name)}
                                                              </p>
                                                              <span className="text-[10px] font-semibold text-gray-500">
                                                                {loadingParts
                                                                  ? 'Loading...'
                                                                  : `${parts.length} part${
                                                                      parts.length === 1 ? '' : 's'
                                                                    }`}
                                                              </span>
                                                            </div>
                                                            <div className="max-h-44 overflow-y-auto">
                                                              {loadingParts ? (
                                                                <div className="flex justify-center py-3">
                                                                  <FaSpinner className="animate-spin text-primary-600" />
                                                                </div>
                                                              ) : parts.length ? (
                                                                <table className="w-full text-[11px]">
                                                                  <thead className="bg-gray-50 sticky top-0">
                                                                    <tr className="text-gray-600">
                                                                      <th className="text-left px-3 py-2 font-semibold">
                                                                        Spare part
                                                                      </th>
                                                                      <th className="text-left px-3 py-2 font-semibold">
                                                                        Part #
                                                                      </th>
                                                                      <th className="text-left px-3 py-2 font-semibold">
                                                                        Notes
                                                                      </th>
                                                                      <th className="text-right px-3 py-2 font-semibold">
                                                                        Status
                                                                      </th>
                                                                      <th className="text-right px-3 py-2 font-semibold">
                                                                        Actions
                                                                      </th>
                                                                    </tr>
                                                                  </thead>
                                                                  <tbody className="divide-y divide-gray-100">
                                                                    {parts.map((part) => (
                                                                      <tr key={part._id || part.id}>
                                                                        {editingPart &&
                                                                        String(editingPart._id) ===
                                                                          String(part._id || part.id) ? (
                                                                          <>
                                                                            <td className="px-3 py-1">
                                                                              <input
                                                                                type="text"
                                                                                value={editingPart.name || ''}
                                                                                onChange={(e) =>
                                                                                  setEditingPart((prev) => ({
                                                                                    ...(prev || {}),
                                                                                    name: e.target.value
                                                                                  }))
                                                                                }
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded text-[11px]"
                                                                              />
                                                                              <div className="mt-1 space-y-1">
                                                                                <label className="inline-flex items-center gap-2 text-[10px] text-primary-700 font-semibold cursor-pointer">
                                                                                  <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    multiple
                                                                                    className="hidden"
                                                                                    disabled={saving || uploadingPartImages}
                                                                                    onChange={async (e) => {
                                                                                      await uploadPartImages(e.target.files);
                                                                                      e.target.value = '';
                                                                                    }}
                                                                                  />
                                                                                  {uploadingPartImages
                                                                                    ? 'Uploading images...'
                                                                                    : 'Upload image'}
                                                                                </label>
                                                                                {!!(
                                                                                  Array.isArray(editingPart.imageUrls) &&
                                                                                  editingPart.imageUrls.length
                                                                                ) && (
                                                                                  <div className="flex flex-wrap gap-1">
                                                                                    {editingPart.imageUrls.map((url) => (
                                                                                      <span
                                                                                        key={url}
                                                                                        className="inline-flex items-center gap-1 text-[10px] bg-gray-100 px-1.5 py-0.5 rounded"
                                                                                      >
                                                                                        img
                                                                                        <button
                                                                                          type="button"
                                                                                          className="text-gray-500 hover:text-red-600"
                                                                                          onClick={() =>
                                                                                            setEditingPart((prev) => {
                                                                                              if (!prev) return prev;
                                                                                              const nextUrls = (
                                                                                                prev.imageUrls || []
                                                                                              ).filter((u) => u !== url);
                                                                                              return {
                                                                                                ...prev,
                                                                                                imageUrls: nextUrls,
                                                                                                imageUrl: nextUrls[0] || ''
                                                                                              };
                                                                                            })
                                                                                          }
                                                                                        >
                                                                                          x
                                                                                        </button>
                                                                                      </span>
                                                                                    ))}
                                                                                  </div>
                                                                                )}
                                                                              </div>
                                                                            </td>
                                                                            <td className="px-3 py-1">
                                                                              <input
                                                                                type="text"
                                                                                value={editingPart.partNumber || ''}
                                                                                onChange={(e) =>
                                                                                  setEditingPart((prev) => ({
                                                                                    ...(prev || {}),
                                                                                    partNumber: e.target.value
                                                                                  }))
                                                                                }
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded text-[11px]"
                                                                              />
                                                                            </td>
                                                                            <td className="px-3 py-1">
                                                                              <input
                                                                                type="text"
                                                                                value={editingPart.notes || ''}
                                                                                onChange={(e) =>
                                                                                  setEditingPart((prev) => ({
                                                                                    ...(prev || {}),
                                                                                    notes: e.target.value
                                                                                  }))
                                                                                }
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded text-[11px]"
                                                                              />
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right">
                                                                              <span className="text-[10px] font-semibold text-gray-600">
                                                                                {editingPart.isActive ? 'Active' : 'Off'}
                                                                              </span>
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                                              <button
                                                                                type="button"
                                                                                onClick={savePartEdit}
                                                                                className="text-primary-700 font-semibold px-1"
                                                                              >
                                                                                Save
                                                                              </button>
                                                                              <button
                                                                                type="button"
                                                                                onClick={() => setEditingPart(null)}
                                                                                className="text-gray-500 px-1"
                                                                              >
                                                                                Cancel
                                                                              </button>
                                                                            </td>
                                                                          </>
                                                                        ) : (
                                                                          <>
                                                                            <td className="px-3 py-2 text-gray-800 font-medium">
                                                                              {dash(part.name)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-gray-700">
                                                                              {dash(part.partNumber)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-gray-700">
                                                                              {dash(part.notes)}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right">
                                                                              <span className="text-[10px] font-semibold text-gray-600">
                                                                                {part.isActive ? 'Active' : 'Off'}
                                                                              </span>
                                                                            </td>
                                                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                                              <button
                                                                                type="button"
                                                                                title="View part"
                                                                                onClick={() =>
                                                                                  setViewPartModal({
                                                                                    ...part,
                                                                                    modelName: mod.name,
                                                                                    categoryName: cat.name
                                                                                  })
                                                                                }
                                                                                className="text-slate-600 p-1 hover:text-slate-800"
                                                                              >
                                                                                <FaEye />
                                                                              </button>
                                                                              <button
                                                                                type="button"
                                                                                title="Edit part"
                                                                                onClick={() =>
                                                                                  setEditingPart({
                                                                                    ...part,
                                                                                    _id: part._id || part.id,
                                                                                    categoryId,
                                                                                    modelId: mod._id
                                                                                  })
                                                                                }
                                                                                className="text-primary-600 p-1 hover:text-primary-700"
                                                                              >
                                                                                <FaEdit />
                                                                              </button>
                                                                              <button
                                                                                type="button"
                                                                                title="Toggle active"
                                                                                onClick={() =>
                                                                                  togglePartActive(part, categoryId, mod._id)
                                                                                }
                                                                                className="text-gray-500 p-1 text-[10px] hover:text-gray-700"
                                                                              >
                                                                                {part.isActive ? 'off' : 'on'}
                                                                              </button>
                                                                              <button
                                                                                type="button"
                                                                                title="Delete part"
                                                                                onClick={() =>
                                                                                  deleteCatalogPart(
                                                                                    part,
                                                                                    categoryId,
                                                                                    mod._id
                                                                                  )
                                                                                }
                                                                                className="text-red-500 p-1 hover:text-red-600"
                                                                              >
                                                                                <FaTrash />
                                                                              </button>
                                                                            </td>
                                                                          </>
                                                                        )}
                                                                      </tr>
                                                                    ))}
                                                                  </tbody>
                                                                </table>
                                                              ) : (
                                                                <p className="text-xs text-gray-500 px-3 py-3">
                                                                  No spare parts in this category.
                                                                </p>
                                                              )}
                                                            </div>
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                      {String(expandedModelId) === String(mod._id) && (
                                        <tr>
                                          <td colSpan={8} className="bg-gray-50/70 p-0">
                                            <div className="px-3 pb-4 pt-3 border-t border-gray-200">
                                              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                                                <div className="px-3 py-2.5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
                                                  <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                                                    Year / month / variant - {mod.name}
                                                  </p>
                                                  <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                    {(trimsByModel[mod._id] || []).length} row(s)
                                                  </span>
                                                </div>
                                                <div className="p-3 space-y-3">
                                                  <div className="rounded-lg border border-gray-200 bg-gray-50/60">
                                                    <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                                                      <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wide">
                                                        Products mapped to {mod.name}
                                                      </p>
                                                      <span className="text-[10px] font-semibold text-gray-500">
                                                        {(productsByModel[mod._id] || []).length} product(s)
                                                      </span>
                                                    </div>
                                                    <div className="max-h-44 overflow-y-auto">
                                                      {loadingProductsFor[mod._id] ? (
                                                        <div className="flex justify-center py-4">
                                                          <FaSpinner className="animate-spin text-primary-600" />
                                                        </div>
                                                      ) : (productsByModel[mod._id] || []).length ? (
                                                        <table className="w-full text-[11px]">
                                                          <thead className="bg-white sticky top-0">
                                                            <tr className="text-gray-600">
                                                              <th className="text-left px-3 py-2 font-semibold">Product</th>
                                                              <th className="text-left px-3 py-2 font-semibold">Part #</th>
                                                              <th className="text-left px-3 py-2 font-semibold">Category</th>
                                                              <th className="text-right px-3 py-2 font-semibold">Price</th>
                                                              <th className="text-right px-3 py-2 font-semibold">Stock</th>
                                                              <th className="text-right px-3 py-2 font-semibold">Status</th>
                                                            </tr>
                                                          </thead>
                                                          <tbody className="divide-y divide-gray-200">
                                                            {(productsByModel[mod._id] || []).map((p) => (
                                                              <tr key={p._id}>
                                                                <td className="px-3 py-2 text-gray-800 font-medium">
                                                                  {dash(p.name)}
                                                                </td>
                                                                <td className="px-3 py-2 text-gray-700">
                                                                  {dash(p.partNumber || p.sku)}
                                                                </td>
                                                                <td className="px-3 py-2 text-gray-700">
                                                                  {dash(p.category)}
                                                                </td>
                                                                <td className="px-3 py-2 text-right text-gray-800">
                                                                  ₹{Number(p.price || 0).toLocaleString()}
                                                                </td>
                                                                <td className="px-3 py-2 text-right text-gray-800">
                                                                  {Number(p.stock || 0).toLocaleString()}
                                                                </td>
                                                                <td className="px-3 py-2 text-right">
                                                                  <span className="text-[10px] font-semibold text-gray-600">
                                                                    {dash(p.status)}
                                                                  </span>
                                                                </td>
                                                              </tr>
                                                            ))}
                                                          </tbody>
                                                        </table>
                                                      ) : (
                                                        <p className="text-xs text-gray-500 px-3 py-3">
                                                          No products added for this model yet.
                                                        </p>
                                                      )}
                                                    </div>
                                                  </div>
                                                  <form
                                                    onSubmit={onAddTrim}
                                                    className="grid grid-cols-1 md:grid-cols-8 gap-2 items-end"
                                                  >
                                                    <div className="md:col-span-2">
                                                      <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                        Generation
                                                      </label>
                                                      <input
                                                        type="text"
                                                        placeholder="e.g. 6th gen"
                                                        value={newTrim.generation}
                                                        onChange={(e) =>
                                                          setNewTrim({
                                                            ...newTrim,
                                                            generation: e.target.value
                                                          })
                                                        }
                                                        className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs"
                                                      />
                                                    </div>
                                                    <div className="md:col-span-1">
                                                      <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                        Year
                                                      </label>
                                                      <input
                                                        type="number"
                                                        min={1980}
                                                        max={2100}
                                                        value={newTrim.year}
                                                        onChange={(e) =>
                                                          setNewTrim({
                                                            ...newTrim,
                                                            year: e.target.value
                                                          })
                                                        }
                                                        className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs"
                                                      />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                      <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                        Month
                                                      </label>
                                                      <select
                                                        value={newTrim.month}
                                                        onChange={(e) =>
                                                          setNewTrim({
                                                            ...newTrim,
                                                            month: Number(e.target.value)
                                                          })
                                                        }
                                                        className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs"
                                                      >
                                                        {MONTHS.map((mo) => (
                                                          <option key={mo.value} value={mo.value}>
                                                            {mo.label}
                                                          </option>
                                                        ))}
                                                      </select>
                                                    </div>
                                                    <div className="md:col-span-2">
                                                      <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                                        Variant
                                                      </label>
                                                      <input
                                                        type="text"
                                                        placeholder="e.g. VDi, ZX"
                                                        value={newTrim.variant}
                                                        onChange={(e) =>
                                                          setNewTrim({
                                                            ...newTrim,
                                                            variant: e.target.value
                                                          })
                                                        }
                                                        className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs"
                                                      />
                                                    </div>
                                                    <button
                                                      type="submit"
                                                      disabled={saving}
                                                      className="md:col-span-1 h-9 px-3 rounded-lg bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 disabled:opacity-50"
                                                    >
                                                      Add row
                                                    </button>
                                                  </form>
                                                  <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                                                <table className="w-full text-xs">
                                                  <thead className="bg-gray-50 sticky top-0">
                                                    <tr>
                                                      <th className="text-left px-3 py-2 font-semibold text-gray-600">
                                                        Generation
                                                      </th>
                                                      <th className="text-left px-3 py-2 font-semibold text-gray-600">
                                                        Year
                                                      </th>
                                                      <th className="text-left px-3 py-2 font-semibold text-gray-600">
                                                        Month
                                                      </th>
                                                      <th className="text-left px-3 py-2 font-semibold text-gray-600">
                                                        Variant
                                                      </th>
                                                      <th className="text-right px-3 py-2 font-semibold text-gray-600">
                                                        {' '}
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-gray-100">
                                                    {(trimsByModel[mod._id] || []).map((t) => (
                                                      <tr key={t._id} className={!t.isActive ? 'opacity-50' : ''}>
                                                        {editingTrim &&
                                                        String(editingTrim._id) === String(t._id) ? (
                                                          <>
                                                            <td className="px-2 py-1">
                                                              <input
                                                                type="text"
                                                                className="w-full border rounded px-1 py-0.5"
                                                                value={editingTrim.generation || ''}
                                                                onChange={(e) =>
                                                                  setEditingTrim({
                                                                    ...editingTrim,
                                                                    generation: e.target.value
                                                                  })
                                                                }
                                                              />
                                                            </td>
                                                            <td className="px-2 py-1">
                                                              <input
                                                                type="number"
                                                                className="w-full border rounded px-1 py-0.5"
                                                                value={editingTrim.year}
                                                                onChange={(e) =>
                                                                  setEditingTrim({
                                                                    ...editingTrim,
                                                                    year: e.target.value
                                                                  })
                                                                }
                                                              />
                                                            </td>
                                                            <td className="px-2 py-1">
                                                              <select
                                                                className="w-full border rounded px-1 py-0.5"
                                                                value={editingTrim.month}
                                                                onChange={(e) =>
                                                                  setEditingTrim({
                                                                    ...editingTrim,
                                                                    month: Number(e.target.value)
                                                                  })
                                                                }
                                                              >
                                                                {MONTHS.map((mo) => (
                                                                  <option
                                                                    key={mo.value}
                                                                    value={mo.value}
                                                                  >
                                                                    {mo.label}
                                                                  </option>
                                                                ))}
                                                              </select>
                                                            </td>
                                                            <td className="px-2 py-1">
                                                              <input
                                                                type="text"
                                                                className="w-full border rounded px-1 py-0.5"
                                                                value={editingTrim.variant}
                                                                onChange={(e) =>
                                                                  setEditingTrim({
                                                                    ...editingTrim,
                                                                    variant: e.target.value
                                                                  })
                                                                }
                                                              />
                                                            </td>
                                                            <td className="px-3 py-1 text-right whitespace-nowrap">
                                                              <button
                                                                type="button"
                                                                onClick={saveTrimEdit}
                                                                className="text-primary-700 font-semibold px-1"
                                                              >
                                                                Save
                                                              </button>
                                                              <button
                                                                type="button"
                                                                onClick={() => setEditingTrim(null)}
                                                                className="text-gray-500 px-1"
                                                              >
                                                                Cancel
                                                              </button>
                                                            </td>
                                                          </>
                                                        ) : (
                                                          <>
                                                            <td className="px-3 py-2 text-gray-700 font-medium">
                                                              {dash(t.generation)}
                                                            </td>
                                                            <td className="px-3 py-2 font-mono text-gray-800">
                                                              {t.year}
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-700">
                                                              {monthLabel(t.month)}
                                                            </td>
                                                            <td className="px-3 py-2 font-medium text-gray-900">
                                                              {t.variant}
                                                            </td>
                                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                              <button
                                                                type="button"
                                                                onClick={() =>
                                                                  setEditingTrim({ ...t })
                                                                }
                                                                className="text-primary-600 p-1 hover:text-primary-700"
                                                                title="Edit"
                                                              >
                                                                <FaEdit />
                                                              </button>
                                                              <button
                                                                type="button"
                                                                onClick={() => toggleTrimActive(t)}
                                                                className="text-gray-500 p-1 text-[10px] hover:text-gray-700"
                                                                title="Toggle active"
                                                              >
                                                                {t.isActive ? 'off' : 'on'}
                                                              </button>
                                                              <button
                                                                type="button"
                                                                onClick={() => deleteTrim(t)}
                                                                className="text-red-500 p-1 hover:text-red-600"
                                                                title="Delete"
                                                              >
                                                                <FaTrash />
                                                              </button>
                                                            </td>
                                                          </>
                                                        )}
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                                {!loadingTrimsFor[mod._id] &&
                                                  !(trimsByModel[mod._id] || []).length && (
                                                    <p className="text-center py-4 text-gray-500 text-xs">
                                                      No variant rows yet.
                                                    </p>
                                                  )}
                                                {loadingTrimsFor[mod._id] && (
                                                  <div className="flex justify-center py-4">
                                                    <FaSpinner className="animate-spin text-primary-600" />
                                                  </div>
                                                )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  )}
                                    </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
              })}
            </tbody>
          </table>
        </div>
        {!filteredMakers.length && !loadingMakers && (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            {makers.length ? 'No makers match the current filters.' : 'No car makers yet.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleCatalog;
